import { FaroText } from "@faro-lotv/flat-ui";
import { Box } from "@mui/system";
import { useMemo, type JSX } from "react";
import { DebugOverlaySection } from "./debug-overlay-section";
import {
  NodeStats,
  PointCloudStats,
  PointCloudStatsMap,
} from "./point-cloud-stats-collector";

type PointCloudStatsProps = {
  pointCloudStatsMap: PointCloudStatsMap;
};

/**
 * @returns A table displaying statistics about nodes and points in the point clouds.
 */
export function PointCloudStatsView({
  pointCloudStatsMap,
}: PointCloudStatsProps): JSX.Element {
  const totals = useMemo(() => {
    const total: PointCloudStats = {
      gpu: { nodes: 0, points: 0 },
      loading: { nodes: 0, points: 0 },
      queued: { nodes: 0, points: 0 },
      visible: { nodes: 0, points: 0 },
      memory: { nodes: 0, points: 0 },
      depth: { nodes: 0, points: 0 },
    };

    // Note: In theory, multiple point clouds could have a view on the same point cloud tree
    // In that case, nodes could be counted multiple times
    // This feature is currently not used in the data preparation page though
    Object.values(pointCloudStatsMap).forEach((stats) => {
      addPointCloudStats(total, stats);
    });

    return total;
  }, [pointCloudStatsMap]);

  return (
    <DebugOverlaySection title="Point Cloud Stats" defaultExpanded>
      <Box
        component="table"
        sx={{
          textAlign: "right",
          borderSpacing: "0.5rem 0",
          "& td,th": {
            p: 0,
          },
        }}
      >
        <tbody>
          <PointCloudStatsRow name="Gpu" stats={totals.gpu} />
          <PointCloudStatsRow name="Visible" stats={totals.visible} />
          <PointCloudStatsRow name="Memory" stats={totals.memory} />
          <PointCloudStatsRow name="Loading" stats={totals.loading} />
          <PointCloudStatsRow name="Queued" stats={totals.queued} />
        </tbody>
      </Box>
    </DebugOverlaySection>
  );
}

type PointCloudStatsRowProps = {
  name: string;
  stats: NodeStats;
};

function PointCloudStatsRow({
  name,
  stats,
}: PointCloudStatsRowProps): JSX.Element | null {
  if (stats.nodes === 0) return null;

  return (
    <tr>
      <th scope="col">
        <FaroText dark variant="bodyS" sx={{ fontWeight: "bold" }}>
          {name}
        </FaroText>
      </th>
      <td>
        <FaroText dark variant="bodyS">
          {stats.nodes.toLocaleString()} nodes
        </FaroText>
      </td>
      <td>
        {stats.points && (
          <FaroText dark variant="bodyS">
            {stats.points.toLocaleString()} points
          </FaroText>
        )}
      </td>
    </tr>
  );
}

function addPointCloudStats(
  current: PointCloudStats,
  newStats: PointCloudStats,
): void {
  addNodeStats(current.gpu, newStats.gpu);
  addNodeStats(current.loading, newStats.loading);
  addNodeStats(current.queued, newStats.queued);
  addNodeStats(current.visible, newStats.visible);
  addNodeStats(current.memory, newStats.memory);
  maxNodeStats(current.depth, newStats.depth);
}

function addNodeStats(current: NodeStats, newStats: NodeStats): void {
  current.nodes += newStats.nodes;
  if (current.points) {
    current.points += newStats.points ?? 0;
  } else {
    current.points = newStats.points;
  }
}

function maxNodeStats(current: NodeStats, newStats: NodeStats): void {
  current.nodes = Math.max(current.nodes, newStats.nodes);
  current.points = Math.max(current.points ?? 0, newStats.points ?? 0);
}
